import {
  createTheme,
  PaletteColor,
  PaletteColorOptions,
} from "@mui/material/styles";

/**
 * Theme constants and configuration.
 *
 * These constants are separated from App.tsx to enable Fast Refresh.
 * Non-component exports in the same file as components break HMR.
 */

/**
 * 8-point grid spacing system
 *
 * | Name    | Spacing  | Points |
 * |---------|----------|--------|
 * | 0       | 0        | 0pt    |
 * | quarter | 1px      | 2pt    | (use 0.25)
 * | half    | 0.25rem  | 4pt    | (use 0.5)
 * | 1       | 0.5rem   | 8pt    |
 * | 2       | 1rem     | 16pt   |
 * | 3       | 1.5rem   | 24pt   |
 * | 4       | 2rem     | 32pt   |
 * | 5       | 2.5rem   | 40pt   |
 * | 6       | 3rem     | 48pt   |
 * | 7       | 3.5rem   | 56pt   |
 * | 8       | 4rem     | 64pt   |
 * | 9       | 4.5rem   | 72pt   |
 * | 10      | 5rem     | 80pt   |
 * | 12      | 6rem     | 96pt   |
 * | 14      | 7rem     | 112pt  |
 * | 16      | 8rem     | 128pt  |
 * | 18      | 9rem     | 144pt  |
 * | 20      | 10rem    | 160pt  |
 *
 * Usage: theme.spacing(1) = 8px, theme.spacing(2) = 16px, etc.
 * For quarter spacing: theme.spacing(0.25) = 2px
 * For half spacing: theme.spacing(0.5) = 4px
 */
export const SPACING_UNIT = 8; // 8-point grid base unit in pixels

/**
 * Corner Radius design tokens
 *
 * | Size   | Value | Use case                          |
 * |--------|-------|-----------------------------------|
 * | small  | 8px   | Small elements, small screens     |
 * | medium | 16px  | Big elements, smaller screens     |
 * | large  | 24px  | Big elements, big screens         |
 */
export const BORDER_RADIUS = {
  small: 8,
  medium: 16,
  large: 24,
} as const;

/**
 * Brand Color Palette
 *
 * Three color families: Dahlia (green), Bellflower (purple), Daisy (yellow/gold)
 * Each family has shades: 900 (darkest), 700, 500, 300, 100, 50 (lightest)
 *
 * Usage guidelines:
 * - Dahlia: Primary brand color, success states, positive feedback
 * - Bellflower: Secondary/info, notes, neutral information
 * - Daisy: Warnings, attention, caution states
 *
 * UI Pattern (from Figma):
 * - Background: [color]50
 * - Border: [color]300
 * - Text/Icon: [color]700
 */
export const COLORS = {
  // Dahlia (Green) - Primary brand color
  dahlia: {
    900: "#1C4321", // Darkest - primary text, headers
    700: "#2E7831", // Dark - text on light backgrounds
    500: "#7EC254", // Medium - accents
    300: "#AFE57F", // Light - borders, highlights
    100: "#DBF4C2", // Very light - subtle backgrounds
    50: "#F7FCF1", // Lightest - card backgrounds
  },
  // Bellflower (Purple) - Secondary/info color
  bellflower: {
    900: "#433765", // Darkest
    700: "#7D5DA3", // Dark - text on light backgrounds
    500: "#B69BDE", // Medium - accents
    300: "#D3C9E7", // Light - borders
    100: "#E0DAEE", // Very light - subtle backgrounds
    50: "#F1EFF7", // Lightest - card backgrounds
  },
  // Daisy (Yellow/Gold) - Warning/accent color
  daisy: {
    900: "#4A411F", // Darkest
    700: "#A48137", // Dark - text on light backgrounds
    500: "#EFC52E", // Medium - accents
    300: "#F7E880", // Light - borders
    100: "#FAF1B6", // Very light - subtle backgrounds
    50: "#FDFBEB", // Lightest - card backgrounds
  },
} as const;

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    drawTool: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    dahlia: Record<keyof typeof COLORS.dahlia, string>;
    bellflower: Record<keyof typeof COLORS.bellflower, string>;
    daisy: Record<keyof typeof COLORS.daisy, string>;
    carbon: PaletteColor;
    drawTool: PaletteColor;
  }
  interface PaletteOptions {
    dahlia?: Partial<Record<keyof typeof COLORS.dahlia, string>>;
    bellflower?: Partial<Record<keyof typeof COLORS.bellflower, string>>;
    daisy?: Partial<Record<keyof typeof COLORS.daisy, string>>;
    carbon?: PaletteColorOptions;
    drawTool?: PaletteColorOptions;
  }
}

const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor: string) =>
  augmentColor({ color: { main: mainColor } });

const spacing = (value: number) => `${SPACING_UNIT * value}px`;

export const theme = createTheme({
  palette: {
    // Primary: Dahlia green (main brand color)
    primary: {
      main: COLORS.dahlia[700],
      light: COLORS.dahlia[500],
      dark: COLORS.dahlia[900],
      contrastText: "#FFFFFF",
    },
    // Secondary: Bellflower purple
    secondary: {
      main: COLORS.bellflower[700],
      light: COLORS.bellflower[300],
      dark: COLORS.bellflower[900],
      contrastText: "#FFFFFF",
    },
    // Warning: Daisy yellow/gold
    warning: {
      main: COLORS.daisy[500],
      light: COLORS.daisy[300],
      dark: COLORS.daisy[700],
      contrastText: COLORS.daisy[900],
    },
    // Success: Dahlia green (lighter shade)
    success: {
      main: COLORS.dahlia[700],
      light: COLORS.dahlia[300],
      dark: COLORS.dahlia[900],
      contrastText: "#FFFFFF",
    },
    // Info: Bellflower purple
    info: {
      main: COLORS.bellflower[500],
      light: COLORS.bellflower[100],
      dark: COLORS.bellflower[700],
      contrastText: COLORS.bellflower[900],
    },
    // Error: Keep MUI default red for critical errors
    error: {
      main: "#D32F2F",
      light: "#EF5350",
      dark: "#C62828",
    },
    // Background colors
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    // Text colors
    text: {
      primary: COLORS.dahlia[900],
      secondary: "#6B7280", // Neutral gray for labels/secondary text
    },
    // Brand color palettes (full access)
    dahlia: COLORS.dahlia,
    bellflower: COLORS.bellflower,
    daisy: COLORS.daisy,
    // Legacy colors (to be phased out - keep for backwards compatibility)
    carbon: createColor("#C89F3E"),
    drawTool: createColor("#FF0000"),
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700, // Inter Bold - for headlines
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 700, // Inter Bold - for headlines
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h4: {
      fontSize: "1rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h5: {
      fontSize: "0.875rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h6: {
      fontSize: "0.75rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400, // Inter Regular - for body text
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400, // Inter Regular - for body text
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500, // Inter Medium
      textTransform: "none",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400, // Inter Regular
    },
    overline: {
      fontSize: "0.625rem",
      fontWeight: 500, // Inter Medium - for uppercase
      textTransform: "uppercase",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    // Buttons - small radius (8px)
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Cards - medium radius (16px) for larger elements
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.medium,
        },
      },
    },
    // Paper - small radius (8px) as default
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Dialogs - medium radius (16px) for larger elements
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: BORDER_RADIUS.medium,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    // Chips - small radius (8px)
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Text fields - small radius (8px)
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Alerts - small radius (8px)
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
          marginRight: spacing(2),
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  // 8-point grid spacing: theme.spacing(1) = 8px
  spacing: SPACING_UNIT,
  // Corner radius: small=8px (default), medium=16px, large=24px
  shape: {
    borderRadius: BORDER_RADIUS.small, // Default to small (8px)
  },
});
